import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import FeaturedMusic from './components/FeaturedMusic';
import MusicLibrary from './components/MusicLibrary';
import UpcomingReleases from './components/UpcomingReleases';
import AboutArtist from './components/AboutArtist';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://canadiannightmares.com';

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'MusicGroup',
      '@id': `${SITE_URL}/#musicgroup`,
      name: 'Canadian Nightmares',
      url: SITE_URL,
      image: 'https://static.readdy.ai/image/4290f71dfbaa025f6a9f1c6e0e6bc4ce/ca8beac7fb2d325d3e1a06ffdb5d3223.png',
      description: 'Canadian Nightmares creates dark electronic and industrial music for the resistance. Combining rock, electronic production, and dramatic themes to create atmospheric soundscapes from the frozen north.',
      genre: ['Dark Electronic', 'Industrial', 'Darkwave', 'Rock'],
      sameAs: [
        'https://www.instagram.com/canadiannightmares/',
        'https://music.youtube.com/channel/UCRHeGip8UYcbnkGqumRjV8Q',
        'https://open.spotify.com/artist/4KZKossrGlEQi3uNx4UhBr',
      ],
    },
    {
      '@type': 'MusicAlbum',
      '@id': `${SITE_URL}/#album-controlled-demolition`,
      name: 'Controlled Demolition',
      byArtist: { '@id': `${SITE_URL}/#musicgroup` },
      datePublished: '2025',
      numTracks: 11,
      track: [
        { '@type': 'MusicRecording', name: 'Not Yours To Take', url: 'https://open.spotify.com/track/5GQO1K1ZAxDfygNfs2fFQx' },
        { '@type': 'MusicRecording', name: 'Cashed Out', url: 'https://open.spotify.com/track/1UjpwacYhLSAGEfINSmpeQ' },
        { '@type': 'MusicRecording', name: 'Just Say NO', url: 'https://open.spotify.com/track/08ZOhR1D3Qud3yLeNdp4Ol' },
        { '@type': 'MusicRecording', name: 'WHO Ended Freedom', url: 'https://open.spotify.com/track/7I6NmoiqIMvax01xrOAqCj' },
        { '@type': 'MusicRecording', name: "We Don't Count", url: 'https://open.spotify.com/track/1kNYipXTileM7nbH8Gl4Ig' },
        { '@type': 'MusicRecording', name: 'Mr.Tariffic', url: 'https://open.spotify.com/track/3Rum57m8zUdwf4W31Szox2' },
        { '@type': 'MusicRecording', name: 'Propaganda Machine', url: 'https://open.spotify.com/track/3CSeVNNUQXJbQsfAycpdv6' },
        { '@type': 'MusicRecording', name: 'By The Numbers', url: 'https://open.spotify.com/track/0UgKdhlLkjdhsECkUnPCYN' },
        { '@type': 'MusicRecording', name: 'Just Comply (FUCK YOU)', url: 'https://open.spotify.com/track/37mrzqdcqVq5twGOhVdr3Y' },
        { '@type': 'MusicRecording', name: 'Boiling Point', url: 'https://open.spotify.com/track/2DuhOZVa2EsiIjm2SHbwAr' },
        { '@type': 'MusicRecording', name: 'Free Canada', url: 'https://open.spotify.com/track/5YmogB8JGd1wg7J2ROZWEb' },
      ],
    },
    {
      '@type': 'MusicAlbum',
      '@id': `${SITE_URL}/#album-actors-on-the-hill`,
      name: 'Actors on the Hill',
      byArtist: { '@id': `${SITE_URL}/#musicgroup` },
      datePublished: '2026',
      numTracks: 1,
      track: [
        { '@type': 'MusicRecording', name: 'Actors on the Hill', url: 'https://open.spotify.com/track/63Zz0eVYHYZxfccREmthik' },
      ],
    },
    {
      '@type': 'MusicAlbum',
      '@id': `${SITE_URL}/#album-welcome-to-the-republic`,
      name: 'Welcome to the "Republic"',
      byArtist: { '@id': `${SITE_URL}/#musicgroup` },
      datePublished: '2026',
      numTracks: 1,
      track: [
        { '@type': 'MusicRecording', name: 'Welcome to the "Republic"', url: 'https://open.spotify.com/track/2B8yQ8gknUkmKhczK96nyd' },
      ],
    },
    {
      '@type': 'MusicAlbum',
      '@id': `${SITE_URL}/#album-lines-on-a-map`,
      name: 'Lines on a Map',
      byArtist: { '@id': `${SITE_URL}/#musicgroup` },
      datePublished: '2025',
      numTracks: 1,
      track: [
        { '@type': 'MusicRecording', name: 'Lines on a Map', url: 'https://open.spotify.com/album/7tz9XYrjQ2EZdPYvKDmTWy' },
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/#webpage`,
      url: SITE_URL,
      name: 'Canadian Nightmares | Music for the Resistance – Dark Electronic & Industrial',
      description: 'Canadian Nightmares creates dark electronic and industrial music for the resistance. Stream albums, singles, and tracks on Spotify, Apple Music, and YouTube.',
      isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, url: SITE_URL, name: 'Canadian Nightmares' },
      about: { '@id': `${SITE_URL}/#musicgroup` },
      inLanguage: 'en-CA',
    },
  ],
};

export default function HomePage() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);

    const scriptId = 'schema-home';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className={`min-h-screen bg-[#0a0a0a] text-white transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
      <div className="grain-overlay"></div>
      <div className="fog-overlay"></div>

      <Hero />
      <FeaturedMusic />
      <MusicLibrary />
      <UpcomingReleases />
      <AboutArtist />
      <Footer />
    </div>
  );
}
